import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { SupervisoryFormData } from '../../types/SupervisoryFormData'
import { SupervisoryFormField } from '../../types/SupervisoryFormField'
import { SupervisoryAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../helpers'

/**
 * Supervisory Review subsection adapter
 * Handles all compliance and evaluation questions in the supervisory review section
 */
export class SupervisoryReviewAdapter extends BaseSubsectionAdapter<SupervisoryFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SupervisoryFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Employee and compliance fields
    const employee =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMPLOYEE
      )?.at(0) || undefined

    const idBadge =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE
      )?.at(0) || undefined

    const honoringRights =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS
      )?.at(0) || undefined

    const emergencyProtocol =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL
      )?.at(0) || undefined

    const hygienePrecautions =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS
      )?.at(0) || undefined

    const reportingChanges =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES
      )?.at(0) || undefined

    const grievanceComplaint =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT
      )?.at(0) || undefined

    const pocDocReviewedWithCaregiver =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
      )?.at(0) || undefined

    const ppeAccess =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS
      )?.at(0) || undefined

    // Supervision documentation fields
    const supervisionObserved =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED
      )?.at(0) || undefined

    const education =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION
      )?.at(0) || undefined

    const verballyUnderstood =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_VERBALLY_UNDERSTOOD
      )?.at(0) || undefined

    const repeatedBack =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPEATED_BACK
      )?.at(0) || undefined

    const demonstratedSkillCorrectness =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_DEMONSTRATED_SKILL_CORRECTNESS
      )?.at(0) || undefined

    const documentation =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_DOCUMENTATION
      )?.at(0) || undefined

    const adheredToCarePlan =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ADHERED_TO_CARE_PLAN
      )?.at(0) || undefined

    const professionalism =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PROFESSIONALISM
      )?.at(0) || undefined

    const satisfaction =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SATISFACTION
      )?.at(0) || undefined

    const comments =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS
      )?.at(0) || undefined

    return {
      [SupervisoryFormField.EMPLOYEE]: employee,
      [SupervisoryFormField.ID_BADGE]: idBadge,
      [SupervisoryFormField.HONORING_RIGHTS]: honoringRights,
      [SupervisoryFormField.EMERGENCY_PROTOCOL]: emergencyProtocol,
      [SupervisoryFormField.HYGIENE_PRECAUTIONS]: hygienePrecautions,
      [SupervisoryFormField.REPORTING_CHANGES]: reportingChanges,
      [SupervisoryFormField.GRIEVANCE_COMPLAINT]: grievanceComplaint,
      [SupervisoryFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]: pocDocReviewedWithCaregiver,
      [SupervisoryFormField.PPE_ACCESS]: ppeAccess,
      [SupervisoryFormField.SUPERVISION_OBSERVED]: supervisionObserved,
      [SupervisoryFormField.EDUCATION]: education,
      [SupervisoryFormField.VERBALLY_UNDERSTOOD]: verballyUnderstood,
      [SupervisoryFormField.REPEATED_BACK]: repeatedBack,
      [SupervisoryFormField.DEMONSTRATED_SKILL_CORRECTNESS]: demonstratedSkillCorrectness,
      [SupervisoryFormField.DOCUMENTATION]: documentation,
      [SupervisoryFormField.ADHERED_TO_CARE_PLAN]: adheredToCarePlan,
      [SupervisoryFormField.PROFESSIONALISM]: professionalism,
      [SupervisoryFormField.SATISFACTION]: satisfaction,
      [SupervisoryFormField.COMMENTS]: comments
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Employee and compliance fields
    if (this.formDataFieldExists(SupervisoryFormField.EMPLOYEE)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMPLOYEE,
        value: this.getFormDataField(SupervisoryFormField.EMPLOYEE)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ID_BADGE)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE,
        value: this.getFormDataField(SupervisoryFormField.ID_BADGE)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.HONORING_RIGHTS)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS,
        value: this.getFormDataField(SupervisoryFormField.HONORING_RIGHTS)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.EMERGENCY_PROTOCOL)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL,
        value: this.getFormDataField(SupervisoryFormField.EMERGENCY_PROTOCOL)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.HYGIENE_PRECAUTIONS)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS,
        value: this.getFormDataField(SupervisoryFormField.HYGIENE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.REPORTING_CHANGES)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES,
        value: this.getFormDataField(SupervisoryFormField.REPORTING_CHANGES)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.GRIEVANCE_COMPLAINT)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT,
        value: this.getFormDataField(SupervisoryFormField.GRIEVANCE_COMPLAINT)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.POC_DOC_REVIEWED_WITH_CAREGIVER)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER,
        value: this.getFormDataField(SupervisoryFormField.POC_DOC_REVIEWED_WITH_CAREGIVER)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.PPE_ACCESS)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS,
        value: this.getFormDataField(SupervisoryFormField.PPE_ACCESS)
      })
    }

    // Supervision documentation fields
    if (this.formDataFieldExists(SupervisoryFormField.SUPERVISION_OBSERVED)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED,
        value: this.getFormDataField(SupervisoryFormField.SUPERVISION_OBSERVED)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.EDUCATION)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION,
        value: this.getFormDataField(SupervisoryFormField.EDUCATION)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.VERBALLY_UNDERSTOOD)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_VERBALLY_UNDERSTOOD,
        value: this.getFormDataField(SupervisoryFormField.VERBALLY_UNDERSTOOD)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.REPEATED_BACK)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPEATED_BACK,
        value: this.getFormDataField(SupervisoryFormField.REPEATED_BACK)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.DEMONSTRATED_SKILL_CORRECTNESS)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_DEMONSTRATED_SKILL_CORRECTNESS,
        value: this.getFormDataField(SupervisoryFormField.DEMONSTRATED_SKILL_CORRECTNESS)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.DOCUMENTATION)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_DOCUMENTATION,
        value: this.getFormDataField(SupervisoryFormField.DOCUMENTATION)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ADHERED_TO_CARE_PLAN)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ADHERED_TO_CARE_PLAN,
        value: this.getFormDataField(SupervisoryFormField.ADHERED_TO_CARE_PLAN)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.PROFESSIONALISM)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PROFESSIONALISM,
        value: this.getFormDataField(SupervisoryFormField.PROFESSIONALISM)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.SATISFACTION)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SATISFACTION,
        value: this.getFormDataField(SupervisoryFormField.SATISFACTION)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.COMMENTS)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS,
        value: this.getFormDataField(SupervisoryFormField.COMMENTS)
      })
    }

    return mutations
  }
}
